// Desktop top bar. Sticky at the top of the main column on `lg+`,
// hidden on mobile (the AppBar handles mobile).
//
// Slots:
//   - `breadcrumb`   : page title + optional subtitle (the shell
//                      supplies the active module's name).
//   - `trailing`     : cluster of action buttons rendered on the
//                      right (search, notifications, family picker,
//                      profile menu, sign-out). Caller composes.
//
// The bar is a single sticky row with a soft bottom border and
// subtle backdrop blur. On the lg/xl boundary, the rail is `w-20`
// (compact); the bar still sits flush with the main column.

import { type ReactNode } from "react";
import { cn } from "@/lib/utils";

export function DesktopTopBar({
  breadcrumb,
  trailing,
}: {
  breadcrumb?: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <header
      data-desktop-top-bar
      className={cn(
        "sticky top-0 z-20 hidden h-14 items-center gap-3 border-b border-border/60 bg-background/90 px-6 backdrop-blur lg:flex xl:px-8",
      )}
    >
      <div className="min-w-0 flex-1 truncate">{breadcrumb}</div>
      {trailing && (
        <div className="flex shrink-0 items-center gap-1">{trailing}</div>
      )}
    </header>
  );
}

/* Small page-title primitive used by the breadcrumb zone. Renders
 * the active module name + an optional subtitle. The chip-style
 * favicon on the left is a subtle indicator of the active module
 * (matches the rail item). */
export function PageTitle({
  icon,
  title,
  subtitle,
}: {
  icon?: ReactNode;
  title: string;
  subtitle?: string | null;
}) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      {icon && (
        <span
          aria-hidden
          className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary"
        >
          {icon}
        </span>
      )}
      <div className="min-w-0">
        <div className="truncate font-serif text-base font-semibold leading-tight tracking-tight sm:text-lg">
          {title}
        </div>
        {subtitle && (
          <div className="truncate text-xs text-muted-foreground">
            {subtitle}
          </div>
        )}
      </div>
    </div>
  );
}
